import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Shield,
  Bell,
  Link as LinkIcon,
  Save,
  CheckCircle,
  AlertCircle,
  Send
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import ConnectionsSettings from './ConnectionsSettings';
import SenderAddressSettings from '../../components/settings/SenderAddressSettings';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const Settings = () => {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/profile`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      const profile = response.data;
      setProfileData({
        name: profile.name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        address_line1: profile.mailing_address?.line1 || '',
        address_line2: profile.mailing_address?.line2 || '',
        city: profile.mailing_address?.city || '',
        state: profile.mailing_address?.state || '',
        postal_code: profile.mailing_address?.postal_code || ''
      });
    } catch (error) {
      console.error('Profile fetch error:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/profile`, profileData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Profile saved successfully');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="settings-loading">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-stone-900 dark:text-emerald-50">Settings</h1>
        <p className="text-sm text-stone-500 dark:text-emerald-300/70">Manage your account settings and preferences</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User size={16} />
            Profile
          </TabsTrigger>
          <TabsTrigger value="mailing" className="flex items-center gap-2">
            <Send size={16} />
            Mailing
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield size={16} />
            Security
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell size={16} />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="connections" className="flex items-center gap-2">
            <LinkIcon size={16} />
            Connections
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <div className="elite-card p-6">
            <h2 className="text-lg font-semibold text-stone-900 dark:text-emerald-50 mb-6">Personal Information</h2>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={profileData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    disabled
                    className="bg-stone-50 dark:bg-stone-800/30"
                  />
                  <p className="text-xs text-stone-500 dark:text-emerald-300/60">Email cannot be changed</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={profileData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(555) 555-5555"
                />
              </div>

              <hr className="border-stone-200 dark:border-emerald-800/30" />

              <h3 className="text-md font-semibold text-stone-900 dark:text-emerald-50">Mailing Address</h3>
              <p className="text-sm text-stone-500 dark:text-emerald-300/70">This address will be used as your return address for dispute letters.</p>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="address_line1">Street Address</Label>
                  <Input
                    id="address_line1"
                    value={profileData.address_line1}
                    onChange={(e) => handleChange('address_line1', e.target.value)}
                    placeholder="123 Main Street"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address_line2">Apartment, Suite, etc.</Label>
                  <Input
                    id="address_line2"
                    value={profileData.address_line2}
                    onChange={(e) => handleChange('address_line2', e.target.value)}
                    placeholder="Apt 4B"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={profileData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      placeholder="New York"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Select value={profileData.state} onValueChange={(v) => handleChange('state', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((state) => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postal_code">ZIP Code</Label>
                    <Input
                      id="postal_code"
                      value={profileData.postal_code}
                      onChange={(e) => handleChange('postal_code', e.target.value)}
                      placeholder="10001"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleSave} disabled={saving} className="btn-primary">
                  {saving ? (
                    <>Saving...</>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Mailing Tab */}
        <TabsContent value="mailing">
          <div className="space-y-6">
            <SenderAddressSettings />
            
            {/* Delivery Channels Info */}
            <div className="elite-card p-6">
              <h3 className="font-semibold text-stone-900 mb-4">Delivery Channels</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-emerald-600" />
                    <div>
                      <p className="font-medium text-emerald-900">Mail (PostGrid)</p>
                      <p className="text-sm text-emerald-700">USPS postal mail delivery</p>
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">Active</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200">
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-stone-400" />
                    <div>
                      <p className="font-medium text-stone-700">Fax</p>
                      <p className="text-sm text-stone-500">Fax delivery for faster response</p>
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded">Coming Soon</span>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <div className="elite-card p-6">
            <h2 className="text-lg font-semibold text-stone-900 mb-6">Security Settings</h2>
            
            <div className="space-y-6">
              <div className="p-4 bg-stone-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Shield className="text-emerald-500" size={24} />
                    <div>
                      <p className="font-medium text-stone-900">Two-Factor Authentication</p>
                      <p className="text-sm text-stone-500">Add an extra layer of security to your account</p>
                    </div>
                  </div>
                  <Button variant="outline" disabled>
                    Coming Soon
                  </Button>
                </div>
              </div>

              <div className="p-4 bg-stone-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-stone-900">Change Password</p>
                    <p className="text-sm text-stone-500">Update your password regularly for security</p>
                  </div>
                  <Button variant="outline" disabled>
                    Coming Soon
                  </Button>
                </div>
              </div>

              <div className="p-4 bg-stone-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-stone-900">Login Sessions</p>
                    <p className="text-sm text-stone-500">Manage your active sessions</p>
                  </div>
                  <Button variant="outline" disabled>
                    View Sessions
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <div className="elite-card p-6">
            <h2 className="text-lg font-semibold text-stone-900 mb-6">Notification Preferences</h2>
            
            <div className="space-y-4">
              {[
                { label: 'Email notifications for dispute updates', enabled: true },
                { label: 'Email notifications for score changes', enabled: true },
                { label: 'Marketing emails and tips', enabled: false },
                { label: 'SMS notifications', enabled: false }
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                  <span className="text-stone-700">{item.label}</span>
                  <div className={`w-12 h-6 rounded-full transition-colors ${item.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                    <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform m-0.5 ${item.enabled ? 'translate-x-6' : ''}`} />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm text-stone-500 mt-4">Notification preferences feature coming soon.</p>
          </div>
        </TabsContent>

        {/* Connections Tab */}
        <TabsContent value="connections">
          <div className="elite-card p-6">
            <ConnectionsSettings />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
